import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import { AppConstants } from 'src/app/commons/app-constants';

// The single app-wide mobile-web banner: "Open in the LumberLinq app".
//
// A web page cannot reliably know whether the native app is installed (Apple & Google both block
// this), so the copy is install-agnostic and the button just *tries* to open the app:
//   - Android: an intent:// URL with an explicit package — opens the installed app (DeepLinkService
//     then routes to the current path), or follows browser_fallback_url to the Play Store.
//   - iOS: best-effort — re-navigate to the current URL (catches a Universal Link on a share
//     route), and if nothing takes over within ~1.2s, send the user to the get-the-app page.
//     (Apple's own reliable mechanism, the Smart App Banner, needs an App Store ID we don't have.)
//
// Replaces the old app.component-embedded .get-app-banner. Guards, 30-day dismiss memory and the
// androidHandoff / MOBILE_APP_BANNER_ENABLED checks are carried over from that implementation.
@Component({
  selector: 'app-open-in-app-banner',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './open-in-app-banner.component.html',
  styleUrl: './open-in-app-banner.component.css',
})
export class OpenInAppBannerComponent implements OnInit {
  /** Emits whenever the banner shows/hides so the app shell can offset fixed-positioned content. */
  @Output() visibleChange = new EventEmitter<boolean>();

  visible = false;
  platform: 'ios' | 'android' | null = null;

  private storeUrl = '';

  private static readonly DISMISS_KEY = 'll_open_in_app_banner_dismissed';
  private static readonly RESHOW_AFTER_DAYS = 30;

  ngOnInit(): void {
    if (!AppConstants.MOBILE_APP_BANNER_ENABLED) return;
    if (Capacitor.isNativePlatform()) return;
    // This tab is the Android payment-handoff Custom Tab — the user is already in the app.
    if (document.cookie.split('; ').some((c) => c === 'androidHandoff=1')) return;

    const ua = navigator.userAgent || '';
    const isIos = /iphone|ipad|ipod/i.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroid = /android/i.test(ua);
    if (!isIos && !isAndroid) return;
    if (this.isDismissed()) return;

    this.platform = isIos ? 'ios' : 'android';
    this.storeUrl = isIos ? AppConstants.IOS_APP_CTA_URL : AppConstants.ANDROID_APP_CTA_URL;
    this.visible = true;
    // Deferred: emitting synchronously in ngOnInit would change the parent's [ngClass] after it
    // was already checked this CD pass (ExpressionChangedAfterItHasBeenCheckedError in dev).
    Promise.resolve().then(() => this.visibleChange.emit(true));
  }

  private isDismissed(): boolean {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(OpenInAppBannerComponent.DISMISS_KEY);
    } catch {
      return false;
    }
    if (!raw) return false;
    try {
      const d = JSON.parse(raw) as { at: string };
      return (
        Date.now() - new Date(d.at).getTime() <
        OpenInAppBannerComponent.RESHOW_AFTER_DAYS * 24 * 60 * 60 * 1000
      );
    } catch {
      return false;
    }
  }

  openInApp(): void {
    const loc = window.location;
    if (this.platform === 'android') {
      const fallback = encodeURIComponent(this.storeUrl);
      loc.href =
        `intent://${loc.host}${loc.pathname}${loc.search}#Intent;scheme=https;` +
        `package=${AppConstants.ANDROID_PACKAGE_ID};S.browser_fallback_url=${fallback};end`;
      return;
    }
    // iOS best-effort: try the current URL (a share route may be a Universal Link), else store.
    const timer = window.setTimeout(() => { loc.href = this.storeUrl; }, 1200);
    const cancel = () => window.clearTimeout(timer);
    document.addEventListener('visibilitychange', cancel, { once: true });
    window.addEventListener('pagehide', cancel, { once: true });
    loc.assign(loc.href);
  }

  dismiss(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    try {
      localStorage.setItem(
        OpenInAppBannerComponent.DISMISS_KEY,
        JSON.stringify({ at: new Date().toISOString() }),
      );
    } catch { /* private mode — fine, just won't persist */ }
  }
}
